<script setup lang="ts">
/**
 * A number nudged a step at a time: − value +. For a thermostat's setpoint, a
 * quantity, a volume — values changed in small steps rather than typed.
 *
 * The value is a WAI-ARIA spinbutton: it takes the focus, ↑/↓ step once,
 * PageUp/PageDown ten times, Home/End jump to `min`/`max`. The − and + buttons
 * step with the pointer and stay out of the tab order — the spinbutton has the
 * keys. `format` renders the value („21,5 °C“) in tabular digits, so a changing
 * value does not jitter. `null` shows `emptyText`; the first step then lands on
 * `origin`.
 *
 * `v-model` follows every step. `commit` fires once the value has settled —
 * `commitDebounce` ms after the last step, at once on blur or unmount — so a
 * burst of clicks becomes one save or one device command. `disabled` locks the
 * buttons and the keys; the value stays readable.
 */
import { computed, onBeforeUnmount } from 'vue';
import Minus from 'lucide-vue-next/dist/esm/icons/minus.js'; import Plus from 'lucide-vue-next/dist/esm/icons/plus.js';
import { debounce } from '../Helpers/timing';
import { NxIconButton } from '../IconButton';
import { NxTabularNumber } from '../TabularNumber';

const props = withDefaults(
  defineProps<{
    /** Accessible name of the value („Solltemperatur“). */
    label: string;
    min?: number;
    max?: number;
    step?: number;
    /** Where the first step lands while the value is `null`. Defaults to `min`, else 0. */
    origin?: number;
    /** Renders the value; the default is the plain number. */
    format?: (value: number) => string;
    /** Shown while the value is `null`. */
    emptyText?: string;
    disabled?: boolean;
    decreaseLabel?: string;
    increaseLabel?: string;
    /** Milliseconds after the last step before `commit`; 0 commits every step. */
    commitDebounce?: number;
  }>(),
  {
    min: Number.NEGATIVE_INFINITY,
    max: Number.POSITIVE_INFINITY,
    step: 1,
    origin: undefined,
    format: undefined,
    emptyText: '–',
    disabled: false,
    decreaseLabel: 'Verringern',
    increaseLabel: 'Erhöhen',
    commitDebounce: 600,
  }
);

const model = defineModel<number | null>({ required: true });

const emit = defineEmits<{
  /** The value settled after one or more steps. */
  commit: [value: number];
}>();

/** Decimals of the step, so 0.1 + 0.2 lands on 0.3. */
const decimals = computed(() => {
  const text = String(props.step);
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : text.length - dot - 1;
});

function clamp(value: number): number {
  return Math.min(props.max, Math.max(props.min, value));
}

function snap(value: number): number {
  const stepped = props.step > 0 ? Math.round(value / props.step) * props.step : value;
  return Number(clamp(stepped).toFixed(decimals.value));
}

const start = computed(() => props.origin ?? (Number.isFinite(props.min) ? props.min : 0));
const atMin = computed(() => model.value !== null && model.value <= props.min);
const atMax = computed(() => model.value !== null && model.value >= props.max);

const text = computed(() => {
  if (model.value === null) return props.emptyText;
  return props.format ? props.format(model.value) : String(model.value);
});

const commitLater = debounce((value: number) => emit('commit', value), props.commitDebounce);

function set(next: number) {
  if (next === model.value) return;
  model.value = next;
  commitLater(next);
}

/** Moves `count` steps (negative = down); a `null` value first lands on `origin`. */
function stepBy(count: number) {
  if (props.disabled) return;
  if (model.value === null) {
    set(snap(start.value));
    return;
  }
  set(snap(model.value + count * props.step));
}

function onKeydown(event: KeyboardEvent) {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const moves: Record<string, () => void> = {
    ArrowUp: () => stepBy(1),
    ArrowDown: () => stepBy(-1),
    PageUp: () => stepBy(10),
    PageDown: () => stepBy(-10),
    Home: () => {
      if (!props.disabled && Number.isFinite(props.min)) set(snap(props.min));
    },
    End: () => {
      if (!props.disabled && Number.isFinite(props.max)) set(snap(props.max));
    },
  };
  const move = moves[event.key];
  if (!move) return;
  event.preventDefault();
  move();
}

onBeforeUnmount(() => commitLater.flush());
</script>

<template>
  <div
    class="nx-number-stepper"
    :class="{ 'is-disabled': props.disabled, 'is-empty': model === null }"
    data-testid="nx-number-stepper"
  >
    <NxIconButton
      variant="outline"
      height="s"
      tabindex="-1"
      :disabled="props.disabled || atMin"
      :title="props.decreaseLabel"
      :aria-label="props.decreaseLabel"
      data-testid="nx-number-stepper-decrease"
      @click="stepBy(-1)"
    >
      <Minus class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
    <span
      class="nx-number-stepper__value"
      role="spinbutton"
      tabindex="0"
      :aria-label="props.label"
      :aria-valuenow="model ?? undefined"
      :aria-valuetext="text"
      :aria-valuemin="Number.isFinite(props.min) ? props.min : undefined"
      :aria-valuemax="Number.isFinite(props.max) ? props.max : undefined"
      :aria-disabled="props.disabled ? 'true' : undefined"
      data-testid="nx-number-stepper-value"
      @keydown="onKeydown"
      @blur="commitLater.flush()"
    >
      <NxTabularNumber :value="text" />
    </span>
    <NxIconButton
      variant="outline"
      height="s"
      tabindex="-1"
      :disabled="props.disabled || atMax"
      :title="props.increaseLabel"
      :aria-label="props.increaseLabel"
      data-testid="nx-number-stepper-increase"
      @click="stepBy(1)"
    >
      <Plus class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
  </div>
</template>

<style scoped>
/* Tight on purpose: it has to fit „21,5 °C“ between its buttons on a control
   tile half a phone wide. */
.nx-number-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-number-stepper__value {
  flex: 1;
  min-width: 0;
  padding: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-md);
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}
.nx-number-stepper__value:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -1);
}
.nx-number-stepper.is-empty .nx-number-stepper__value,
.nx-number-stepper.is-disabled .nx-number-stepper__value {
  color: var(--nxt-fg-muted);
}

@media (forced-colors: active) {
  .nx-number-stepper__value:focus-visible {
    outline-color: Highlight;
  }
}
</style>
